import React, { useState, useEffect } from 'react';
import {
  Card, CardBody, Typography, Button, Dialog, Input, CardHeader, CardFooter, Tooltip
} from "@material-tailwind/react";
import { ethers } from "ethers";
import abi from "../../utils/AuctionRegistry.json"; // Path to AuctionRegistry ABI
import auctionAbi from "../../utils/MedicalServiceAuction.json"; // Path to MedicalServiceAuction ABI
import { ErrorPopup } from './components/ErrorPopup';
import { SuccessPopup } from './components/SuccessPopup'; // Adjust the path based on your file structure



export function Bids(props) {
  const contractAddress = process.env.REACT_APP_REGISTRY_CONTRACT_ADDRESS; // Registry contract address from env
  const contractABI = abi;
  const auctionContractABI = auctionAbi;

  const [servicesList, setServicesList] = useState([]);
  const [details, setDetails] = useState([]);
  const [open, setOpen] = useState(false);
  const [placeBid, setPlaceBid] = useState({ price: "" });
  // Inside the Bids function
const [errorMessage, setErrorMessage] = useState('');
const [bidSuccess, setBidSuccess] = useState(false);
const [successMessage, setSuccessMessage] = useState('');



  const handleOpen = () => setOpen((cur) => !cur);

  const handleChange = (e) => {
    setPlaceBid({
      ...placeBid,
      [e.target.name]: e.target.value,
    });
  };

  const handlePlaceABid = async (contractAddr, event) => {
    event.preventDefault();
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const auctionContract = new ethers.Contract(
      contractAddr,
      auctionContractABI,
      signer
    );
  
    try {
      const transaction = await auctionContract.placeBid({
        value: placeBid.price,
      });
      // Wait for the transaction to be mined
      await transaction.wait();
      // Reset the place bid state and close the dialog
      setPlaceBid({ price: "" });
      setOpen(false);
      setBidSuccess(true);
      setErrorMessage(''); 
      // Refresh auction details
      populateTable();
      setSuccessMessage('Bid placed successfully!');
    } catch (error) {
      console.error("Error placing bid:", error);
      // Extract and set the error message
    let errorMsg = "Error placing bid. Please try again.";
    if (error.reason) {
      errorMsg = error.reason;
    } else if (error.message) {
      errorMsg = error.message;
    }
    setErrorMessage(errorMsg);
    }
  };

  
  const fetchBidsForAuction = async (auctionAddress) => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const auctionContract = new ethers.Contract(
      auctionAddress,
      auctionContractABI,
      signer
    );

    try {
      const bids = await auctionContract.getAllBids();
      return bids.map(bid => ({
        bidder: bid.bidder,
        amount: ethers.utils.formatEther(bid.amount)
      }));
    } catch (error) {
      console.error(`Error fetching bids for auction at ${auctionAddress}:`, error);
      return [];
    }
  };

  const populateTable = async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();

    const allDetails = await Promise.all(
      servicesList.map(async (auctionAddress) => {
        const auctionContract = new ethers.Contract(
          auctionAddress,
          auctionContractABI,
          signer
        );


        try {
          const auctionDetails = await auctionContract.getAuctionDetails();
          const bids = await fetchBidsForAuction(auctionAddress);

          return {
            patient: auctionDetails._patient,
            service: auctionDetails._service,
            biddingEndTime: new Date(auctionDetails._biddingEndTime * 1000).toLocaleString(),
            minBudget: ethers.utils.formatEther(auctionDetails._minBudget),
            maxBudget: ethers.utils.formatEther(auctionDetails._maxBudget),
            location: auctionDetails._location,
            auctionEnded: auctionDetails._auctionEnded,
            contractAddress: auctionAddress,
            bids: bids,
          };
        } catch (error) {
          console.error(`Error fetching details for auction at address ${auctionAddress}:`, error);
          return null;
        }
      })
    );
    console.log(allDetails)
    setDetails(allDetails.filter((detail) => detail !== null));
  };

  const fetchAllAuctionAddresses = async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const registryContract = new ethers.Contract(
      contractAddress,
      contractABI,
      signer
    );
    console.log(registryContract);
    try {
      const addresses = await registryContract.getAllAuctions();
      console.log(addresses)
      return addresses;
    } catch (error) {
      console.error("Error fetching auction addresses:", error);
      return [];
    }
  };

  useEffect(() => {
    const loadAuctions = async () => {
      const addresses = await fetchAllAuctionAddresses();
      setServicesList(addresses);
    };

    loadAuctions();
  }, []);

  useEffect(() => {
    if (servicesList.length > 0) {
      populateTable();
    }
  }, [servicesList]);

  return (
    <div className="flex flex-wrap">
      {successMessage && <SuccessPopup onClose={() => setSuccessMessage('')} message={successMessage} />}

      {errorMessage && <ErrorPopup onClose={() => setErrorMessage('')} message={errorMessage} />}
    
      {details.map((detail, index) => (
        <div key={index} className="w-full sm:w-1/2 md:w-1/2 lg:w-1/2 p-2">
          <Card className="mx-auto mt-6 w-full sm:w-3/4">
            <CardBody className="flex flex-col justify-center items-center h-full">
              <Typography variant="h6" color="blue-gray" className="mb-2">
                Service: {detail.service}
              </Typography>
              <Tooltip
                content={<div>{detail.contractAddress}</div>}
                interactive={true}
                placement="top"
                offset={5}
              >
                <Typography
                  variant="h6"
                  color="blue-gray"
                  className="mb-2 truncate w-11/12 hover:bg-gray-200"
                >
                  {detail.contractAddress}
                </Typography>
              </Tooltip>
              <Typography>
                <p><strong>Bidding End Time:</strong> {detail.biddingEndTime}</p>
                <p><strong>Budget:</strong> {detail.minBudget} ETH - {detail.maxBudget} ETH</p>
                <p><strong>Location:</strong> {detail.location}</p>
                <p><strong>Auction Ended:</strong> {detail.auctionEnded ? 'Yes' : 'No'}</p>
              </Typography>
              <div className="my-4">
                <Typography variant="h6" color="blue-gray">
                  Bids
                </Typography>
                {detail.bids.map((bid, bidIndex) => (
                  <p key={bidIndex}>
                    {bid.bidder}: {bid.amount} ETH
                  </p>
                ))}
              </div>
              <Button color="teal" onClick={() => handleOpen()}>Place Bid</Button>
            </CardBody>
          </Card>
  
          <Dialog size="xs" open={open} handler={handleOpen}>
            <Card>
              <CardHeader variant="gradient" color="blue">
                <Typography variant="h3" color="white">Place a Bid</Typography>
              </CardHeader>
              <CardBody>
                <Input 
                  label="Price (in ETH)" 
                  name="price" 
                  size="lg" 
                  value={placeBid.price} 
                  onChange={handleChange} 
                  autocomplete="off" 
                />
              </CardBody>
              <CardFooter>
                <Button 
                  variant="gradient" 
                  fullWidth 
                  onClick={(event) => handlePlaceABid(detail.contractAddress, event)}
                >
                  Place Bid
                </Button>
              </CardFooter>
            </Card>
          </Dialog>
        </div>
      ))}
    </div>
  );
  
}

export default Bids;
